import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Share2, Check, ArrowRight, Truck } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { SEO } from '../components/SEO';
import { CustomerPhotoGrid } from '../components/CustomerPhotoGrid';

const API = process.env.REACT_APP_BACKEND_URL;

const RecentDeliveriesPage = () => {
  const { t, i18n } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Map app language → Intl locale
  const intlLocale = (() => {
    const map = { fr: 'fr-CA', en: 'en-CA', es: 'es-ES', ar: 'ar' };
    return map[i18n.language?.split('-')[0]] || 'fr-CA';
  })();

  useEffect(() => {
    const fetchDeliveries = async () => {
      try {
        const pres = await fetch(`${API}/api/customer-photos`);
        const pdata = await pres.json();
        if (pdata.photos?.length) {
          setPhotos(pdata.photos);
          return;
        }
        const res = await fetch(`${API}/api/vehicles/recent-deliveries?days=45&limit=60&min_count=40`);
        const data = await res.json();
        setVehicles(data.vehicles || []);
      } catch (err) {
        console.error('Error fetching deliveries:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDeliveries();
  }, []);

  const handleShare = async (vehicle) => {
    const url = `${window.location.origin}/vehicle/${vehicle.id}`;
    const text = t('recentDeliveries.shareText', { year: vehicle.year, make: vehicle.make, model: vehicle.model });
    try {
      if (navigator.share) {
        await navigator.share({ title: text, url });
      } else {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
      }
    } catch { /* ignore */ }
  };

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString(intlLocale, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return null; }
  };

  const getImageUrl = (vehicle) => {
    const img = vehicle.images?.[0];
    if (!img) return null;
    const url = typeof img === 'string' ? img : img.url;
    if (url?.includes('cloudinary.com')) return url.replace('/upload/', '/upload/w_600,q_auto,f_auto/');
    return url;
  };

  return (
    <>
      <SEO
        title={t('recentDeliveries.metaTitle')}
        description={t('recentDeliveries.metaDescription')}
        url="https://entrepotautomontreal.ca/nos-livraisons"
      />
      <Navbar />

      <div className="min-h-screen bg-[#0a0f1a]">
        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0c2340]/90 via-[#0a0f1a] to-[#0a0f1a]" />
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #3b82f6 0%, transparent 50%), radial-gradient(circle at 80% 50%, #1d4ed8 0%, transparent 50%)' }} />
          <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28 text-center">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              <Truck className="h-4 w-4" />
              {t('recentDeliveries.heroBadge')}
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('recentDeliveries.heroTitle')}
            </h1>
            <p className="text-blue-200/70 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              {t('recentDeliveries.heroSubtitle')}
            </p>
            {!loading && !photos.length && (
              <p className="text-blue-400/50 text-sm mt-4">
                {t('recentDeliveries.countLabel', { count: vehicles.length })}
              </p>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="relative z-10 max-w-6xl mx-auto px-4 pb-20 -mt-4">
          <h2 className="text-base md:text-lg font-bold text-white mb-6 flex items-center gap-3" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="recent-deliveries-subtitle">
            <span className="h-px w-8 bg-blue-500" />
            {t('recentDeliveries.photosSubtitle')}
          </h2>
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
            </div>
          ) : photos.length ? (
            <CustomerPhotoGrid photos={photos} />
          ) : vehicles.length === 0 ? (
            <div className="text-center py-24 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
              <Truck className="h-16 w-16 text-blue-500/30 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">{t('recentDeliveries.emptyTitle')}</h2>
              <p className="text-blue-200/50 mb-6">{t('recentDeliveries.emptySubtitle')}</p>
              <Link to="/inventory" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-blue-700 transition-colors">
                {t('recentDeliveries.ctaSeeInventory')} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {vehicles.map((vehicle) => {
                const deliveryDate = formatDeliveryDate(vehicle.sold_date || vehicle.updated_at);
                const imgUrl = getImageUrl(vehicle);

                return (
                  <div
                    key={vehicle.id}
                    className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden hover:border-blue-500/30 hover:bg-white/[0.07] transition-all duration-300 hover:-translate-y-1"
                    data-testid={`delivery-card-${vehicle.id}`}
                  >
                    {/* Image/Video */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-slate-900">
                      {vehicle.video_url ? (
                        <video
                          ref={(el) => {
                            if (!el) return;
                            const obs = new IntersectionObserver(([e]) => {
                              if (e.isIntersecting) el.play().catch(() => {});
                              else el.pause();
                            }, { threshold: 0.3 });
                            obs.observe(el);
                          }}
                          src={vehicle.video_url.includes('cloudinary.com')
                            ? vehicle.video_url.replace('/upload/', '/upload/w_480,q_auto:eco,br_500k,f_mp4/')
                            : vehicle.video_url}
                          muted loop playsInline preload="metadata"
                          className="w-full h-full object-cover"
                        />
                      ) : imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-800">
                          <Truck className="h-12 w-12 text-slate-600" />
                        </div>
                      )}

                      <div className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
                        {t('recentDeliveries.soldBadge')}
                      </div>

                      <button
                        onClick={(e) => { e.preventDefault(); handleShare(vehicle); }}
                        className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                        data-testid={`share-delivery-${vehicle.id}`}
                      >
                        <Share2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <h3 className="text-white font-bold text-base mb-1 group-hover:text-blue-400 transition-colors">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h3>
                      {vehicle.trim && (
                        <p className="text-blue-300/40 text-xs uppercase mb-3">{vehicle.trim}</p>
                      )}

                      {deliveryDate && (
                        <div className="flex items-center gap-1.5 text-blue-300/50 text-xs mb-3">
                          <Calendar className="h-3 w-3" />
                          <span>{t('recentDeliveries.deliveredOn', { date: deliveryDate })}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <span className="text-emerald-400/70 text-xs font-medium flex items-center gap-1">
                          <Check className="h-3 w-3" /> {t('recentDeliveries.deliveredRecently')}
                        </span>
                        <Link
                          to={`/inventory?make=${encodeURIComponent(vehicle.make)}`}
                          className="text-blue-400 text-xs font-medium flex items-center gap-1 hover:text-blue-300 transition-colors"
                        >
                          {t('recentDeliveries.similarVehicles')} <ArrowRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Bottom SEO Content */}
          <div className="mt-16 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 md:p-12">
            <h2 className="text-xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('recentDeliveries.seoTitle')}
            </h2>
            <div className="grid md:grid-cols-2 gap-6 text-blue-200/60 text-sm leading-relaxed">
              <div>
                <p className="mb-3">{t('recentDeliveries.seoText1a')}</p>
                <p>{t('recentDeliveries.seoText1b')}</p>
              </div>
              <div>
                <p className="mb-3">{t('recentDeliveries.seoText2a')}</p>
                <p>
                  {t('recentDeliveries.seoText2bPrefix')}
                  <Link to="/inventory" className="text-blue-400 hover:text-blue-300 underline">{t('recentDeliveries.seoInventoryLink')}</Link>
                  {t('recentDeliveries.seoText2bMiddle')}
                  <Link to="/contact" className="text-blue-400 hover:text-blue-300 underline">{t('recentDeliveries.seoContactLink')}</Link>
                  {t('recentDeliveries.seoText2bSuffix')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecentDeliveriesPage;
